import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '../../theme/ThemeContext';
import { selectSymbols, selectSymbolsLoading } from '../../redux/features/platformSlice';
import { useFavourites } from '../../hooks/useFavourites';
import Sparkline from '../common/Sparkline';
import { CloseIcon, PlusIcon, SearchIcon } from '../../helpers/Icons';
import Bid from '../common/Bid';
import PriceChange from '../common/PriceChange';
import { ComponentLoader } from '../common/Loading';
import Spread from '../common/Spread';
import { filterSymbolsByRealtimePrice, getMarketWatchSymbols, getStoredMarketWatchSymbols } from '../../utils/symbolPriceFilter';

const CATS = ['All', 'Forex', 'Crypto', 'Stocks', 'Indices', 'Commodities'];
const MARKET_WATCH_STORAGE_KEY = 'market_watch_symbols';
const DEFAULT_MARKET_WATCH_COUNT = 12;



const saveMarketWatchSymbols = (symbols) => {
  try {
    localStorage.setItem(MARKET_WATCH_STORAGE_KEY, JSON.stringify(symbols));
  } catch {}
};

const SymbolRow = ({ sym, isActive, isFav, onClick, onFav, onRemove }) => {

  const theme = useTheme();
  const [hov, setHov] = useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{
      display: 'flex', alignItems: 'center', gap: 7, padding: '5px 8px', cursor: 'pointer',
      borderRadius: 7, margin: '1px 4px',
      background: isActive ? theme.primaryGlow : hov ? theme.card2 : 'transparent',
      borderLeft: `2px solid ${isActive ? theme.primary : 'transparent'}`,
      transition: 'background 0.12s',
    }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', background: theme.card2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 7, fontWeight: 700, color: theme.textS, letterSpacing: '-0.5px', flexShrink: 0, overflow: 'hidden' }}>
        {sym?.icon_url
          ? <img src={sym.icon_url} alt={sym.symbol} style={{ width: '28px', height: '28px', objectFit: 'contain', borderRadius: '50%' }} onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
          : null
        }
        <span style={{ display: sym?.icon_url ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          {sym.symbol.slice(0, 3)}
        </span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: theme.text }}>{sym.symbol}</span>
          <Bid symbol={sym.symbol} digits={sym.digits} style={{ fontSize: 11, fontWeight: 600, }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 1 }}>
          <Spread symbol={sym.symbol} digits={sym.digits} style={{ fontSize: 9, fontWeight: 600 }} />
          <PriceChange symbol={sym.symbol} digits={sym.digits} style={{ fontSize: 9, fontWeight: 600 }} />
        </div>
      </div>
      <Sparkline symbol={sym.symbol} width={40} height={18} positiveColor={theme.pos} negativeColor={theme.neg} />
      {/* <button onClick={e => { e.stopPropagation(); onFav(sym.symbol); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0, opacity: isFav ? 1 : 0.22 }}>
        <StarIcon s={12} c={isFav ? theme.orange : theme.textM} f={isFav ? theme.orange : 'none'} />
      </button> */}
      {/* i want close button showing when i hover over the symbol */}
      <button title="Hide symbol" onClick={e => { e.stopPropagation(); onRemove(sym.symbol); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0, opacity: hov ? 1 : 0, transition: 'opacity 0.12s', pointerEvents: hov ? 'auto' : 'none' }}>
        <CloseIcon s={12} c={theme.textM} />
      </button>
    </div>
  );
};

const SymbolList = ({ activeSymbol, onSelectSymbol }) => {
  const theme = useTheme();
  const symbols = useSelector(selectSymbols);
  const symbolsLoading = useSelector(selectSymbolsLoading);
  const { symbols: favs, toggle: toggleFav } = useFavourites();
  const [search, setSearch] = useState('');
  const [addSearch, setAddSearch] = useState('');
  const [cat, setCat] = useState('All');
  const [isAdding, setIsAdding] = useState(false);
  const [marketWatchSymbols, setMarketWatchSymbols] = useState(getStoredMarketWatchSymbols);
  // console.log("=========== symbol ===========", symbols)

  const allSymbols = useMemo(
    () => filterSymbolsByRealtimePrice(symbols),
    [symbols]
  );

  React.useEffect(() => {
    if (marketWatchSymbols !== null || !allSymbols.length) return;

    const initialSymbols = allSymbols
      .slice(0, DEFAULT_MARKET_WATCH_COUNT)
      .map(s => s.symbol)
      .filter(Boolean);

    setMarketWatchSymbols(initialSymbols);
    saveMarketWatchSymbols(initialSymbols);
  }, [allSymbols, marketWatchSymbols]);

  const pricedSymbols = useMemo(
    () => getMarketWatchSymbols(symbols, marketWatchSymbols),
    [symbols, marketWatchSymbols]
  );

  const marketWatchSet = useMemo(
    () => new Set(pricedSymbols.map(s => s.symbol)),
    [pricedSymbols]
  );

  const addableSymbols = useMemo(() => {
    const needle = addSearch.trim().toLowerCase();
    return allSymbols
      .filter(s => !marketWatchSet.has(s.symbol))
      .filter(s => {
        if (!needle) return true;
        return s.symbol?.toLowerCase().includes(needle) || s.description?.toLowerCase().includes(needle);
      })
      .slice(0, 60);
  }, [allSymbols, addSearch, marketWatchSet]);

  const addMarketSymbol = (sym) => {
    const current = marketWatchSymbols || pricedSymbols.map(s => s.symbol);
    const next = Array.from(new Set([...current, sym.symbol]));
    setMarketWatchSymbols(next);
    saveMarketWatchSymbols(next);
    setAddSearch('');
    setIsAdding(false);
    onSelectSymbol(sym);
  };

  const removeMarketSymbol = (symbol) => {
    const current = marketWatchSymbols || pricedSymbols.map(s => s.symbol);
    const next = current.filter(sym => sym !== symbol);
    setMarketWatchSymbols(next);
    saveMarketWatchSymbols(next);

    if (activeSymbol === symbol) {
      const nextSymbol = pricedSymbols.find(s => s.symbol !== symbol);
      if (nextSymbol) onSelectSymbol(nextSymbol);
    }
  };

  const filtered = useMemo(() =>
    pricedSymbols.filter(s => {
      const mc = cat === 'All' || s.market?.toLowerCase() === cat.toLowerCase();
      const ms = s.symbol?.toLowerCase().includes(search.toLowerCase()) || s.description?.toLowerCase().includes(search.toLowerCase());
      return mc && ms;
    }), [search, cat, pricedSymbols]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: theme.card }}>
      <div style={{ padding: '7px 7px', borderBottom: `1px solid ${theme.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: theme.card2, borderRadius: 7, padding: '5px 9px', flex: 1, minWidth: 0 }}>
            <SearchIcon s={12} c={theme.textM} />
            <input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
              style={{ background: 'none', border: 'none', outline: 'none', color: theme.text, fontSize: 11, fontFamily: theme.sans, flex: 1, minWidth: 0 }} />
          </div>
          <button title="Add symbol" onClick={() => setIsAdding(true)} style={{
            width: 28, height: 28, borderRadius: 7, border: `1px solid ${theme.border}`,
            background: theme.primary, color: '#fff', cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <PlusIcon s={14} c="currentColor" />
          </button>
        </div>
      </div>
      {isAdding && (
        <div style={{ borderBottom: `1px solid ${theme.border}`, background: theme.surface, padding: 7 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 7 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: theme.card2, borderRadius: 7, padding: '5px 9px', flex: 1, minWidth: 0 }}>
              <SearchIcon s={12} c={theme.textM} />
              <input autoFocus placeholder="Search symbols to add..." value={addSearch} onChange={e => setAddSearch(e.target.value)}
                style={{ background: 'none', border: 'none', outline: 'none', color: theme.text, fontSize: 11, fontFamily: theme.sans, flex: 1, minWidth: 0 }} />
            </div>
            <button title="Close" onClick={() => { setIsAdding(false); setAddSearch(''); }} style={{
              width: 28, height: 28, borderRadius: 7, border: `1px solid ${theme.border}`,
              background: theme.card, cursor: 'pointer', display: 'flex', alignItems: 'center',
              justifyContent: 'center', flexShrink: 0
            }}>
              <CloseIcon s={13} c={theme.textS} />
            </button>
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto', border: `1px solid ${theme.border}`, borderRadius: 7, background: theme.card }}>
            {addableSymbols.length === 0 ? (
              <div style={{ padding: 14, fontSize: 10, color: theme.textM, textAlign: 'center' }}>
                {addSearch ? 'No matching symbols' : 'All symbols are already shown'}
              </div>
            ) : addableSymbols.map(sym => (
              <button key={sym.symbol} onClick={() => addMarketSymbol(sym)} style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 8, padding: '7px 9px', border: 'none', borderBottom: `1px solid ${theme.border}66`,
                background: 'transparent', color: theme.text, cursor: 'pointer', fontFamily: theme.sans,
                textAlign: 'left'
              }}>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 11, fontWeight: 700 }}>{sym.symbol}</span>
                  <span style={{ display: 'block', fontSize: 9, color: theme.textM, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sym.description || sym.market}</span>
                </span>
                <PlusIcon s={12} c={theme.primary} />
              </button>
            ))}
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 2, padding: '4px 5px', borderBottom: `1px solid ${theme.border}`, overflowX: 'auto', scrollbarWidth: 'none' }}>
        {CATS.map(c => (
          <button key={c} onClick={() => setCat(c)} style={{
            padding: '3px 6px', borderRadius: 5, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
            fontSize: 9, fontWeight: 600, fontFamily: theme.sans,
            background: cat === c ? theme.primary : 'transparent', color: cat === c ? '#fff' : theme.textS,
          }}>{c}</button>
        ))}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '3px 0' }}>
        {symbolsLoading ? (
          <ComponentLoader message="Fetching assets..." />
        ) : (
          <>
            {cat === 'All' && favs.length > 0 && <>
              <div style={{ padding: '4px 10px', fontSize: 8, fontWeight: 700, color: theme.textM, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Favorites</div>
              {favs.map(sym => {
                const s = pricedSymbols.find(x => x.symbol === sym); if (!s) return null;
                return <SymbolRow key={sym} sym={s} isActive={activeSymbol === sym} isFav={true} onClick={() => onSelectSymbol(s)} onFav={toggleFav} onRemove={removeMarketSymbol} />;
              })}
              <div style={{ height: 1, background: theme.border, margin: '3px 10px' }} />
            </>}

            {filtered.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '40px 20px', gap: 12 }}>
                <div style={{ fontSize: 28, opacity: 0.15, fontWeight: 900, fontFamily: theme.mono, color: theme.text, letterSpacing: '-1px', lineHeight: 1 }}>
                  {cat === 'All' ? '∅' : cat.slice(0, 3).toUpperCase()}
                </div>
                <div style={{ width: 36, height: 1, background: theme.border }} />
                <div style={{ fontSize: 11, fontWeight: 700, color: theme.textS, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  No {cat === 'All' ? 'symbols' : cat} shown
                </div>
                <div style={{ fontSize: 9, color: theme.textM, textAlign: 'center', lineHeight: 1.6, maxWidth: 160 }}>
                  {search
                    ? `No results for "${search}"`
                    : `${cat === 'All' ? 'Add symbols' : `Add ${cat.toLowerCase()} instruments`} to Market Watch`
                  }
                </div>
                {search && (
                  <button onClick={() => setSearch('')} style={{
                    marginTop: 4, padding: '4px 12px', borderRadius: 5, border: `1px solid ${theme.border}`,
                    background: 'transparent', color: theme.textS, fontSize: 9, fontWeight: 600,
                    cursor: 'pointer', fontFamily: theme.sans, letterSpacing: '0.05em'
                  }}>
                    CLEAR SEARCH
                  </button>
                )}
                {!search && (
                  <button onClick={() => setIsAdding(true)} style={{
                    marginTop: 4, padding: '5px 12px', borderRadius: 5, border: `1px solid ${theme.border}`,
                    background: theme.primary, color: '#fff', fontSize: 9, fontWeight: 700,
                    cursor: 'pointer', fontFamily: theme.sans, letterSpacing: '0.05em'
                  }}>
                    ADD SYMBOL
                  </button>
                )}
              </div>
            ) : (
              filtered.map(sym => (
                <SymbolRow key={sym.symbol} sym={sym}
                  isActive={activeSymbol === sym.symbol} isFav={favs.includes(sym.symbol)}
                  onClick={() => onSelectSymbol(sym)} onFav={toggleFav} onRemove={removeMarketSymbol} />
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
};
export default SymbolList;
